import { useEffect, useState, useRef } from "react";
import { Button, Form } from "react-bootstrap";
import { useRebalanceBoards } from "./boards";

interface BoardSelectorProps {
  selectedBoardId: number | null;
  onSelect: (boardId: number | null, query?: string) => void;
  onManageClick: () => void;
}

const BoardSelector = ({
  selectedBoardId,
  onSelect,
  onManageClick,
}: BoardSelectorProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Every board, walked page by page from `GET /v1/rebalances/boards`.
  const { boards, isPending: isLoadingBoards } = useRebalanceBoards();

  // Save selection to localStorage
  useEffect(() => {
    if (selectedBoardId !== null) {
      localStorage.setItem(
        "rebalances-selected-board-id",
        selectedBoardId.toString(),
      );
    } else {
      localStorage.removeItem("rebalances-selected-board-id");
    }
  }, [selectedBoardId]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleBoardSelect = (boardId: number | null) => {
    if (boardId === null) {
      onSelect(null);
    } else {
      const selectedBoard = boards.find((b) => b.id === boardId);
      onSelect(boardId, selectedBoard?.query);
    }
    setIsOpen(false);
  };

  if (isLoadingBoards) {
    return (
      <div className="column-search-item mb-3">
        <div className="input-group input-group-sm">
          <span className="input-group-text">Boards</span>
          <Form.Control
            value="Loading boards..."
            readOnly
            disabled
            className="multi-selector-box"
          />
        </div>
      </div>
    );
  }

  const selectedBoard = boards.find((b) => b.id === selectedBoardId);
  const displayValue = selectedBoard ? selectedBoard.name : "No board";

  return (
    <div className="column-search-item mb-3">
      <div className="input-group input-group-sm">
        <span className="input-group-text">Boards</span>
        <div style={{ position: "relative", flex: 1 }} ref={dropdownRef}>
          <Form.Control
            value={displayValue}
            readOnly
            placeholder="All boards"
            className="multi-selector-box"
            style={{
              cursor: "pointer",
              borderRadius: "0",
              paddingTop: "0.25rem",
              paddingBottom: "0.25rem",
              fontSize: "0.875rem",
            }}
            onClick={() => setIsOpen(!isOpen)}
          />
          {isOpen && (
            <div
              className="dropdown-menu show"
              style={{
                position: "absolute",
                top: "100%",
                left: "0",
                right: "0",
                borderTop: "none",
                borderRadius: "0 0 0.375rem 0.375rem",
                maxHeight: "200px",
                overflowY: "auto",
                zIndex: 1000,
              }}
            >
              <div
                className="dropdown-item"
                style={{ cursor: "pointer" }}
                onClick={() => handleBoardSelect(null)}
              >
                No board
              </div>
              {boards.map((board) => (
                <div
                  key={board.id}
                  className="dropdown-item"
                  style={{ cursor: "pointer" }}
                  onClick={() => handleBoardSelect(board.id)}
                >
                  {board.name}
                </div>
              ))}
            </div>
          )}
        </div>
        <Button variant="primary" onClick={onManageClick}>
          Manage
        </Button>

        {boards.length === 0 && (
          <Form.Text className="text-muted">No boards available.</Form.Text>
        )}
      </div>
    </div>
  );
};

export default BoardSelector;
